import { Filters, MetaData, ExamActiveFilters } from "toptaal-shared"
import { z } from "zod"

const ExamTypeOwnerEnum = z.enum(["cito", "toptaal", "vnt2"], {
  message: "Eigenaar is verplicht",
})

const examTypeOwner: Record<z.infer<typeof ExamTypeOwnerEnum>, string> = {
  cito: "Cito",
  toptaal: "TopTaal",
  vnt2: "VNT2",
}

const examTypeOptionsOwner: {
  value: z.infer<typeof ExamTypeOwnerEnum>
  label: string
}[] = [
  { value: "toptaal", label: "TopTaal" },
  { value: "cito", label: "Cito" },
  { value: "vnt2", label: "VNT2" },
]

const ExamTypeCategoryEnum = z.enum(
  ["intake", "level_test", "learnability", "not_applicable"],
  {
    message: "Categorie is verplicht",
  },
)

const examTypeOptionsCategory: {
  value: z.infer<typeof ExamTypeCategoryEnum>
  label: string
}[] = [
  { value: "intake", label: "Intake" },
  { value: "level_test", label: "Niveautoets" },
  { value: "learnability", label: "Leerbaarheid" },
  { value: "not_applicable", label: "Niet van toepassing" },
]

const ExamTypeSkillEnum = z.enum(
  [
    "conversation",
    "speaking",
    "reading",
    "listening",
    "writing",
    "not_applicable",
  ],
  {
    message: "Vaardigheid is verplicht",
  },
)

const examTypeOptionsSkill: {
  value: z.infer<typeof ExamTypeSkillEnum>
  label: string
}[] = [
  { value: "conversation", label: "Gesprekken" },
  { value: "speaking", label: "Spreken" },
  { value: "reading", label: "Lezen" },
  { value: "listening", label: "Luisteren" },
  { value: "writing", label: "Schrijven" },
  { value: "not_applicable", label: "Niet van toepassing" },
]

const ExamTypeLevelEnum = z.enum(
  [
    "alpha",
    "a1_minus",
    "a1",
    "a2_minus",
    "a2",
    "b1_minus",
    "b1",
    "b2_minus",
    "b2",
    "c1_minus",
    "c1",
    "entry",
    "1f",
    "2f",
    "3f",
    "4f",
    "nvt",
  ],
  {
    message: "Niveau is verplicht",
  },
)

const examTypeOptionsLevel: {
  value: z.infer<typeof ExamTypeLevelEnum>
  label: string
}[] = [
  { value: "alpha", label: "Alfa" },
  { value: "a1_minus", label: "A1-" },
  { value: "a1", label: "A1" },
  { value: "a2_minus", label: "A2-" },
  { value: "a2", label: "A2" },
  { value: "b1_minus", label: "B1-" },
  { value: "b1", label: "B1" },
  { value: "b2_minus", label: "B2-" },
  { value: "b2", label: "B2" },
  { value: "c1_minus", label: "C1-" },
  { value: "c1", label: "C1" },
  { value: "entry", label: "Instroom" },
  { value: "1f", label: "1F" },
  { value: "2f", label: "2F" },
  { value: "3f", label: "3F" },
  { value: "4f", label: "4F" },
  { value: "nvt", label: "N.V.T." },
]

type ExamTypeOwner = z.infer<typeof ExamTypeOwnerEnum>
type ExamTypeCategory = z.infer<typeof ExamTypeCategoryEnum>
type ExamTypeSkill = z.infer<typeof ExamTypeSkillEnum>
type ExamTypeLevel = z.infer<typeof ExamTypeLevelEnum>

interface Exam {
  id: number
  title: string
  owner: ExamTypeOwner
  category: ExamTypeCategory
  skill: ExamTypeSkill
  level: ExamTypeLevel
}

type Exams = Exam[]

interface ModuleExamAssignment {
  examId: number
  lessonNumber: number
}

interface PaginatedExamsResponse {
  data: {
    exams: Exams
  }
  meta: MetaData
}

export interface ExamFilters extends Filters {
  owner?: ExamTypeOwner
  category?: ExamTypeCategory
  skill?: ExamTypeSkill
  level?: ExamTypeLevel
  filters?: ExamActiveFilters
}

const getExamLabel = (
  options: Array<{ value: string; label: string }>,
  value?: string | null,
) => options.find((option) => option.value === value)?.label ?? "-"

export type {
  Exam,
  Exams,
  ExamTypeOwner,
  PaginatedExamsResponse,
  ModuleExamAssignment,
}
export {
  ExamTypeOwnerEnum,
  ExamTypeCategoryEnum,
  ExamTypeSkillEnum,
  ExamTypeLevelEnum,
}
export {
  getExamLabel,
  examTypeOwner,
  examTypeOptionsOwner,
  examTypeOptionsCategory,
  examTypeOptionsSkill,
  examTypeOptionsLevel,
}
